// Component: DeploymentSection.jsx
// Purpose: Deployment controls for Step 4 sidebar — mode toggle, deploy/stop, version info,
//          deploy-button REQUIRED-readiness gate (strategy-deploy-readiness-checklist R5.4/R5.5)

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { FiPlay, FiSquare, FiAlertTriangle, FiCheck, FiLoader, FiFileText, FiRadio, FiShield, FiEye, FiSliders, FiLock, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { Tooltip, Badge, Input, Select, Toggle } from '../../../../../components/ds';
import api, { services } from '../../../../../services/api';
import { getBrokerBadge } from '../../../../../utils/tradingTransforms';
import { eodCloseOwnerLabel } from '../../../../../utils/closeOwner';  //! #118 WP9 (D41) — one definition, both surfaces

/*>>> Shared account-name resolver — every deploy-panel surface shows the holder + broker, NOT the
     raw account UUID (Deepak). Fetches the accounts list once (guarded — absent in some test mocks). <<<*/
const useAccountNames = () => {
    const [map, setMap] = useState({});
    useEffect(() => {
        const getAccounts = services?.brokerAccounts?.getAccounts;
        if (typeof getAccounts !== 'function') return undefined;
        let cancelled = false;
        const req = getAccounts();
        if (!req || typeof req.then !== 'function') return undefined;
        req.then((res) => {
            if (cancelled) return;
            const list = res?.data?.data ?? res?.data ?? [];
            const m = {};
            (Array.isArray(list) ? list : []).forEach((a) => {
                if (a?.account_id) m[a.account_id] = {
                    name: a.account_holder_name || a.nickname || a.broker_user_id || null,
                    broker: a.broker_display_name || null,
                };
            });
            setMap(m);
        }).catch(() => { if (!cancelled) setMap({}); });
        return () => { cancelled = true; };
    }, []);
    return map;
};

//--> #118 WP6 (D41) — the deployment's selected account(s)' broker name(s), deduped, for the
//    "Broker square-off (<broker>)" label. `draftOrders` is keyed by account_id (the deployment's
//    selected accounts — see StrategyBuilder.jsx's own draftOrders prop comment); `names` is the
//    useAccountNames() map ({ [account_id]: { name, broker } }). Returns [] when unresolved (the
//    caller falls back to a generic phrase rather than block on a lookup miss).
const brokerNamesForDeployment = (draftOrders, names) => {
    const ids = Object.keys(draftOrders || {});
    const brokers = new Set();
    ids.forEach((id) => {
        const b = names?.[id]?.broker;
        if (b) brokers.add(b);
    });
    return Array.from(brokers);
};

//! #118 WP9 — this local copy of `eodCloseOwnerLabel` is DELETED and imported from
//!  `utils/closeOwner.js` instead. WP6 correctly noted the mapping is "the ONLY two user-facing
//!  labels ... at BOTH frontend sites", then defined it in one of them; WP9's site would have
//!  needed a second copy. Two copies of a money-path label on two surfaces is precisely the
//!  drift this epic spent its decommission budget deleting, so it now lives in exactly one
//!  module that both surfaces import. Behaviour and wording are byte-identical.

//--> "Deep Kumar · Flattrade" from an account_id (falls back to a shortened id if the name isn't known).
const acctDisplay = (id, names) => {
    const info = names?.[id];
    if (info?.name) return info.broker ? `${info.name} · ${info.broker}` : info.name;
    return `Account ${String(id || '').slice(0, 8)}…`;
};

/*>>> MODE TOGGLE <<<*/
const MODES = [
    { key: 'paper', label: 'Paper', icon: FiFileText },
    { key: 'live',  label: 'Live',  icon: FiRadio },
];

const ModeToggle = ({ mode, onChange, disabled }) => {
    const containerRef = useRef(null);
    const buttonRefs = useRef({});
    const [pillStyle, setPillStyle] = useState({});

    const updatePillPosition = useCallback(() => {
        const container = containerRef.current;
        const activeBtn = buttonRefs.current[mode];
        if (!container || !activeBtn) return;
        const cRect = container.getBoundingClientRect();
        const bRect = activeBtn.getBoundingClientRect();
        setPillStyle({
            left: bRect.left - cRect.left,
            width: bRect.width,
            height: bRect.height,
        });
    }, [mode]);

    useEffect(() => { updatePillPosition(); }, [updatePillPosition]);
    useEffect(() => { window.addEventListener('resize', updatePillPosition); return () => window.removeEventListener('resize', updatePillPosition); }, [updatePillPosition]);

    return (
        <div ref={containerRef} className="relative flex bg-bg-3 rounded-xl p-1 border border-[var(--divider)] overflow-hidden">
            {/* Sliding pill */}
            <div
                className={`absolute top-1 rounded-lg transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                    mode === 'paper'
                        ? 'bg-gradient-to-r from-[var(--badge-info-dot)] to-[var(--badge-info-dot)] shadow-sm'
                        : 'bg-gradient-to-r from-[var(--badge-profit-dot)] to-[var(--badge-profit-dot)] shadow-sm'
                }`}
                style={{ ...pillStyle, opacity: pillStyle.width ? 1 : 0 }}
            />
            {MODES.map(m => {
                const Icon = m.icon;
                const isActive = mode === m.key;
                return (
                    <button
                        key={m.key}
                        ref={el => { buttonRefs.current[m.key] = el; }}
                        onClick={() => !disabled && onChange(m.key)}
                        disabled={disabled}
                        className={`relative z-10 flex-1 py-2.5 px-4 text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                            isActive
                                ? 'text-white'
                                : 'text-fg-3 hover:text-fg-1'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <Icon size={14} className={isActive ? 'drop-shadow-sm' : ''} />
                        {m.label}
                    </button>
                );
            })}
        </div>
    );
};

/*>>> VERSION INFO CARD <<<*/
const VersionInfoCard = ({ currentVersionNumber, activeDeployment }) => {
    const isPaper = activeDeployment?.mode === 'paper';

    if (!activeDeployment) {
        return (
            <div className="p-3.5 bg-bg-3 rounded-xl border border-[var(--divider)] text-sm flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-bg-3 flex items-center justify-center">
                    <FiFileText size={14} className="text-fg-3" />
                </div>
                <div>
                    <span className="text-fg-3 text-xs">Editing</span>
                    <div className="text-fg-1 font-bold text-sm">{currentVersionNumber || 'Draft'}</div>
                </div>
            </div>
        );
    }

    const deployedVersion = activeDeployment.version_number;
    const modeLabel = isPaper ? 'Paper Trade' : 'Live Trade';
    const ModeIcon = isPaper ? FiFileText : FiRadio;
    const isSameVersion = currentVersionNumber === deployedVersion;

    //--> Same version — simple compact display
    if (isSameVersion) {
        return (
            <div className="p-3.5 bg-bg-3 rounded-xl border border-[var(--divider)] text-sm space-y-2">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-bg-3 flex items-center justify-center">
                        <FiFileText size={14} className="text-fg-3" />
                    </div>
                    <div>
                        <span className="text-fg-3 text-xs">Editing</span>
                        <div className="text-fg-1 font-bold text-sm">{currentVersionNumber || 'Draft'}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 pt-1 border-t border-[var(--divider)]">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${
                        isPaper
                            ? 'bg-[color-mix(in_srgb,var(--badge-info-dot)_12%,transparent)] text-[var(--badge-info-fg)]'
                            : 'bg-[color-mix(in_srgb,var(--badge-profit-dot)_12%,transparent)] text-[var(--badge-profit-fg)]'
                    }`}>
                        <ModeIcon size={11} />
                        {modeLabel}
                    </span>
                    <span className="text-fg-3 text-xs">This version is deployed</span>
                </div>
            </div>
        );
    }

    //--> Different version — prominent mismatch warning
    return (
        <div className="relative rounded-xl border border-[color-mix(in_oklch,var(--warning)_35%,transparent)] overflow-hidden text-sm">
            {/* Amber accent top bar */}
            <div className="h-0.5 bg-gradient-to-r from-[var(--warning)] to-[var(--warning)]" />

            {/* Warning banner */}
            <div className="flex items-center gap-2 px-3.5 py-2 bg-[var(--badge-warn-bg)]">
                <FiAlertTriangle size={13} className="text-warning flex-shrink-0" />
                <span className="text-xs font-semibold text-warning">
                    You are editing an older version
                </span>
            </div>

            <div className="px-3.5 py-3 bg-bg-3 space-y-2.5">
                {/* Currently viewing */}
                <div className="flex items-center gap-2.5">
                    <FiFileText size={14} className="text-warning flex-shrink-0" />
                    <span className="text-fg-1 font-bold text-sm">{currentVersionNumber || 'Draft'}</span>
                    <span className="text-fg-3 text-xs">viewing</span>
                </div>

                {/* Divider with "deployed" */}
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-[var(--divider)]" />
                    <span className="text-[10px] font-bold text-fg-3 uppercase">deployed</span>
                    <div className="flex-1 h-px bg-[var(--divider)]" />
                </div>

                {/* Deployed version with badge */}
                <div className="flex items-center gap-2.5">
                    <ModeIcon size={14} className={isPaper ? 'text-[var(--badge-info-fg)]' : 'text-[var(--badge-profit-fg)]'} />
                    <span className="text-fg-1 font-bold text-sm">{deployedVersion}</span>
                    <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
                        isPaper
                            ? 'bg-[color-mix(in_srgb,var(--badge-info-dot)_12%,transparent)] text-[var(--badge-info-fg)] border-[color-mix(in_srgb,var(--badge-info-dot)_25%,transparent)]'
                            : 'bg-[color-mix(in_srgb,var(--badge-profit-dot)_12%,transparent)] text-[var(--badge-profit-fg)] border-[color-mix(in_srgb,var(--badge-profit-dot)_25%,transparent)]'
                    }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isPaper ? 'bg-[var(--badge-info-dot)]' : 'bg-[var(--badge-profit-dot)]'}`} />
                        {modeLabel}
                    </span>
                </div>
            </div>
        </div>
    );
};

/*>>> ACTIVE DEPLOYMENT CARD <<<*/
const ActiveDeploymentCard = ({ deployment, hasOpenPositions, isStopping, onStop }) => {
    if (!deployment) return null;

    const isPaper = deployment.mode === 'paper';
    const modeLabel = isPaper ? 'Paper Trade' : 'Live Trade';
    const ModeIcon = isPaper ? FiFileText : FiRadio;
    const startedAt = deployment.started_at ? new Date(deployment.started_at).toLocaleString() : '—';

    return (
        <div className={`relative p-4 rounded-xl border space-y-3 overflow-hidden ${
            isPaper
                ? 'bg-[color-mix(in_srgb,var(--badge-info-dot)_12%,transparent)] border-[color-mix(in_srgb,var(--badge-info-dot)_25%,transparent)]'
                : 'bg-[color-mix(in_srgb,var(--badge-profit-dot)_12%,transparent)] border-[color-mix(in_srgb,var(--badge-profit-dot)_25%,transparent)]'
        }`}>
            {/* Subtle top accent line */}
            <div className={`absolute top-0 left-0 right-0 h-0.5 ${
                isPaper
                    ? 'bg-gradient-to-r from-[var(--badge-info-dot)] to-[var(--badge-info-dot)]'
                    : 'bg-gradient-to-r from-[var(--badge-profit-dot)] to-[var(--badge-profit-dot)]'
            }`} />

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                        isPaper
                            ? 'bg-[color-mix(in_srgb,var(--badge-info-dot)_12%,transparent)] text-[var(--badge-info-fg)]'
                            : 'bg-[color-mix(in_srgb,var(--badge-profit-dot)_12%,transparent)] text-[var(--badge-profit-fg)]'
                    }`}>
                        <ModeIcon size={13} />
                    </div>
                    <div>
                        <span className="font-bold text-sm text-fg-1">{deployment.version_number}</span>
                        <span className={`ml-2 text-xs font-semibold ${
                            isPaper ? 'text-[var(--badge-info-fg)]' : 'text-[var(--badge-profit-fg)]'
                        }`}>{modeLabel}</span>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${
                        isPaper ? 'bg-[var(--badge-info-dot)]' : 'bg-profit'
                    }`} />
                    <span className="text-[10px] font-semibold text-fg-3 uppercase tracking-wider">Running</span>
                </div>
            </div>
            <div className="text-xs text-fg-3 pl-[38px]">Started: {startedAt}</div>

            {hasOpenPositions && (
                <div className="flex items-center gap-2 p-2.5 bg-[var(--badge-warn-bg)] border border-[color-mix(in_oklch,var(--warning)_30%,transparent)] rounded-lg text-xs text-warning">
                    <FiAlertTriangle className="flex-shrink-0" size={13} />
                    <span>Open positions on {deployment.version_number} — close them before switching versions</span>
                </div>
            )}

            <Tooltip content={hasOpenPositions ? 'Close open positions first' : ''} placement="top" className="w-full">
            <button
                type="button"
                onClick={onStop}
                disabled={hasOpenPositions || isStopping}
                className="btn btn-danger w-full"
            >
                {isStopping ? (
                    <><FiLoader className="animate-spin" /> Stopping...</>
                ) : (
                    <><FiSquare size={13} /> Stop {isPaper ? 'Paper Trade' : 'Live Trade'}</>
                )}
            </button>
            </Tooltip>
        </div>
    );
};

/*>>> R7.1 — PER-BROKER-ACCOUNT PROTECTION TRANSPARENCY <<<*/
//! live-exit-lane R7.1 (W6, 02-design.md §1) — genuinely NEW UI (confirmed pre-existing: this
//  file had ZERO account-scoped rendering, 02-data-lineage.md §1). Calls the CONFIRMED
//  GET /protection-routing?strategy_id=<uuid> endpoint (server-side join — never a client-side
//  broker_code join, per the design's explicit AC refinement; see api.js's liveExitLane comment
//  for the path-naming correction against the landed sibling backend package). One row per
//  account; label is rendered VERBATIM from the server response — R7.1's "FT row flip changes
//  the UI string with zero UI code change" AC depends on this component doing NO local
//  synthetic/native string construction of its own (the exact label strings live server-side in
//  protection_routing_display.py's LABEL_SYNTHETIC/LABEL_NATIVE, not duplicated here).
/*>>> Reusable collapsible deploy-panel section (Deepak: declutter the deploy panel) <<<*/
//! One header treatment for every section: chevron + icon + title, an optional one-line summary
//  shown ONLY while collapsed, body revealed on click. Informational sections default collapsed;
//  the editable settings form (the action the operator came for) stays default-open.
const CollapsibleCard = ({ icon: Icon, title, summary, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="space-y-2">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-full flex items-center gap-2 text-left group"
            >
                {open ? <FiChevronDown size={12} className="text-fg-3" /> : <FiChevronRight size={12} className="text-fg-3" />}
                {Icon ? <Icon size={12} className="text-fg-3" /> : null}
                <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider group-hover:text-fg-2">{title}</h5>
                {!open && summary ? (
                    <span className="ml-auto text-[10px] font-medium text-fg-3 whitespace-nowrap">{summary}</span>
                ) : null}
            </button>
            {open ? <div className="space-y-2">{children}</div> : null}
        </div>
    );
};

const ProtectionTransparencySection = ({ strategyId }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    //--> account_id -> human holder name. The /protection-routing row only carries the account UUID;
    //    map it to the holder via the accounts list (the same source the rest of the app uses) so the
    //    row reads "Deep Kumar", not a raw UUID (Deepak). Guarded — absent in some test mocks.
    const [acctNames, setAcctNames] = useState({});

    useEffect(() => {
        if (!strategyId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        services.liveExitLane.getProtectionRouting(strategyId)
            .then((res) => {
                if (cancelled) return;
                //--> Gateway envelope: { status, data: { strategy_id, rows: [...] }, error } per
                //*   CLAUDE.md API Standards AND the CONFIRMED landed contract
                //*   (backend/order-management/app/api/v1/endpoints/protection_routing.py:47-49,
                //*   sibling package, found mid-implementation). Defensive fallbacks kept for a
                //*   bare-array shape (pre-envelope / directly-mocked-in-tests) so this doesn't
                //*   silently render empty if the envelope shape drifts again.
                const payload = res?.data?.data ?? res?.data;
                const data = Array.isArray(payload) ? payload : (payload?.rows ?? []);
                setRows(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (cancelled) return;
                setError('Could not load protection-routing status.');
                console.log('[DIAG] ProtectionTransparencySection fetch error', { err: err?.message });
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [strategyId]);

    useEffect(() => {
        const getAccounts = services?.brokerAccounts?.getAccounts;
        if (typeof getAccounts !== 'function') return;
        let cancelled = false;
        const req = getAccounts();
        if (!req || typeof req.then !== 'function') return;
        req.then((res) => {
            if (cancelled) return;
            const list = res?.data?.data ?? res?.data ?? [];
            const map = {};
            (Array.isArray(list) ? list : []).forEach((a) => {
                if (a?.account_id) map[a.account_id] = a.account_holder_name || a.nickname || a.broker_user_id || null;
            });
            setAcctNames(map);
        }).catch(() => { if (!cancelled) setAcctNames({}); });
        return () => { cancelled = true; };
    }, []);

    if (!strategyId) return null;

    //--> One-line rollup for the collapsed header (mechanism-derived, not the verbatim server label).
    const count = rows.length;
    const allNative = count > 0 && rows.every((r) => r.mechanism === 'native');
    const allSynth  = count > 0 && rows.every((r) => r.mechanism !== 'native');
    const rollup = count === 0 ? '' : allNative ? 'Broker-native' : allSynth ? 'Synthesized' : 'Mixed';
    const summary = loading
        ? <FiLoader size={11} className="animate-spin text-fg-3" />
        : count > 0
            ? (<>{count} account{count === 1 ? '' : 's'} · <span className={allNative ? 'text-[var(--badge-profit-fg)]' : 'text-[var(--badge-info-fg)]'}>{rollup}</span></>)
            : null;

    return (
        <CollapsibleCard icon={FiShield} title="Per-Account Exit Protection" summary={summary}>
            {loading && (
                <div className="text-xs text-fg-3 flex items-center gap-2 px-1">
                    <FiLoader size={11} className="animate-spin" /> Checking broker capabilities…
                </div>
            )}
            {!loading && error && (<div className="text-xs text-warning px-1">{error}</div>)}
            {!loading && !error && rows.length === 0 && (
                <div className="text-xs text-fg-3 px-1">No live accounts allocated yet.</div>
            )}
            {!loading && !error && rows.length > 0 && (
                <div className="space-y-1.5">
                    {rows.map((row) => {
                        const brokerCfg = getBrokerBadge(row.broker);
                        const isNative = row.mechanism === 'native';
                        const name = acctNames[row.account_id];
                        return (
                            <div
                                key={row.account_id}
                                className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-bg-3 border border-[var(--divider)]"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    {brokerCfg
                                        ? <Badge variant={brokerCfg.variant} size="sm">{brokerCfg.label}</Badge>
                                        : <span className="text-fg-2 text-xs">—</span>}
                                    <div className="flex flex-col min-w-0 leading-tight">
                                        <span className="text-xs font-semibold text-fg-1 truncate" title={row.account_id}>
                                            {name || `Account ${String(row.account_id).slice(0, 8)}…`}
                                        </span>
                                        <span className="text-[10px] text-fg-3 truncate">{row.label}</span>
                                    </div>
                                </div>
                                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${
                                    isNative ? 'bg-profit/10 text-profit' : 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)]'
                                }`}>
                                    <FiShield size={9} />{isNative ? 'Broker-native' : 'Synthesized'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </CollapsibleCard>
    );
};

/*>>> R7.2 — CHASE/RE-ARM/EOD SETTINGS FORM <<<*/
//! live-exit-lane R7.2 (W6, 02-design.md §1) — now 6 fields writing to
//  StrategyDeployment.policy_overrides via PATCH /strategies/{id}/deployments/{id}/policy
//  (confirmed backend contract, backend/strategy-management/app/api/v1/endpoints/deployments.py
//  ::DeploymentPolicySettingsRequest, commit 56f55783 — field names + `eod_close_owner` literal
//  values corrected here during Tranche-B integration to match the landed shape exactly;
//  #118 WP6 adds `exit_lead_minutes`).
//  chase_cap_pct/profit_lock_threshold_pct are FRACTIONS (backend validates 0 < x <= 1), not
//  raw counts/percents — e.g. 0.02 = 2% cap, 0.75 = 75% profit-lock threshold.
//! #118 WP6/D41 — this FE default is a DISPLAY-ONLY fallback for the brief window before any
//  GET response has hydrated `initialSettings` (e.g. this form's very first render). It does
//  NOT drive backend behavior: since D41, a genuinely NEW deployment already has an explicit
//  `eod_close_owner:'broker_rms'` PERSISTED at creation time (`deployment_service.deploy()`),
//  so `initialSettings` hydrates to that real stored value almost immediately; a pre-D41
//  legacy deployment (no stored key) is resolved by the OMS-side `DEFAULT_EOD_CLOSE_OWNER`
//  constant (`presquareoff_sweep_job.py`, WP9, R/O-cited), not by this FE constant. Kept
//  aligned with the NEW-deployment default so the transient pre-hydration render never shows
//  the wrong owner.
const DEFAULT_SETTINGS = {
    chase_n: 3,
    chase_interval_s: 30,
    chase_cap_pct: 0.02,
    profit_lock_threshold_pct: '',
    eod_close_owner: 'broker_rms',
    exit_lead_minutes: '',
};

const DeploymentSettingsForm = ({ strategyId, deploymentId, initialSettings, brokerNames }) => {
    const [values, setValues] = useState({ ...DEFAULT_SETTINGS, ...(initialSettings || {}) });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [savedFlash, setSavedFlash] = useState(false);

    //--> Re-hydrate when the caller supplies fresh settings (e.g. after navigating between
    //*   deployments) — never stomps in-progress local edits on an unrelated re-render.
    useEffect(() => {
        if (initialSettings) setValues((prev) => ({ ...prev, ...initialSettings }));
    }, [initialSettings]);

    const handleChange = useCallback((field) => (e) => {
        const raw = e?.target ? e.target.value : e;
        setValues((prev) => ({ ...prev, [field]: raw }));
    }, []);

    const handleSave = useCallback(async () => {
        if (!strategyId || !deploymentId) return;
        setSaving(true);
        setSaveError(null);
        try {
            await services.liveExitLane.updateDeploymentSettings(strategyId, deploymentId, values);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2000);
        } catch (err) {
            setSaveError('Could not save settings — try again.');
            console.log('[DIAG] DeploymentSettingsForm save error', { err: err?.message });
        } finally {
            setSaving(false);
        }
    }, [strategyId, deploymentId, values]);

    if (!deploymentId) return null;

    return (
        <div className="space-y-2.5">
            <div className="flex items-center gap-2">
                <FiSliders size={12} className="text-fg-3" />
                <h5 className="text-[11px] font-bold text-fg-3 uppercase tracking-wider">Exit Chase &amp; Re-arm Settings</h5>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <Input
                    size="sm"
                    label="Chase attempts (N)"
                    type="number"
                    min={0}
                    value={values.chase_n}
                    onChange={handleChange('chase_n')}
                />
                <Input
                    size="sm"
                    label="Chase interval (s)"
                    type="number"
                    min={1}
                    value={values.chase_interval_s}
                    onChange={handleChange('chase_interval_s')}
                />
                <Input
                    size="sm"
                    label="Chase cap (%)"
                    type="number"
                    min={0}
                    max={1}
                    step="0.01"
                    value={values.chase_cap_pct}
                    onChange={handleChange('chase_cap_pct')}
                />
                <Input
                    size="sm"
                    label="Profit-lock threshold (%)"
                    type="number"
                    min={0}
                    max={1}
                    step="0.01"
                    value={values.profit_lock_threshold_pct}
                    onChange={handleChange('profit_lock_threshold_pct')}
                />
            </div>

            <Select
                size="sm"
                label="EOD close owner"
                value={values.eod_close_owner}
                onChange={handleChange('eod_close_owner')}
            >
                <option value="broker_rms">{eodCloseOwnerLabel('broker_rms', brokerNames)}</option>
                <option value="platform_first">{eodCloseOwnerLabel('platform_first', brokerNames)}</option>
            </Select>

            {values.eod_close_owner === 'platform_first' && (
                //--> #118 WP6 — only meaningful under 'platform_first' (broker mode has no
                //*   DeeTrade pre-close lead); hidden otherwise to avoid implying it applies.
                <Input
                    size="sm"
                    label="Pre-cutoff exit lead (minutes)"
                    type="number"
                    min={1}
                    value={values.exit_lead_minutes}
                    onChange={handleChange('exit_lead_minutes')}
                />
            )}

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="btn btn-secondary btn-sm"
                >
                    {saving ? <><FiLoader className="animate-spin" size={12} /> Saving…</> : 'Save settings'}
                </button>
                {savedFlash && <span className="text-xs text-profit">Saved</span>}
                {saveError && <span className="text-xs text-loss">{saveError}</span>}
            </div>
        </div>
    );
};

/*>>> R7.3 — EXIT-LANE NOTIFICATION VERBOSITY TOGGLE <<<*/
//! live-exit-lane R7.3 (W6, 02-design.md §1) — `exit_lane_verbosity`, a dormant JSONB slot in
//  `user_trading_settings.settings` (setting_type="notification_rules"). Tranche-B integration
//  fix (2026-07-24): W6-FRONTEND originally shipped this as local-component-state only because
//  no read/write endpoint existed for this setting_type at dispatch time (only `/rollover*` was
//  registered) — discovered + closed during integration: GET/PUT
//  /trading-settings/notification-rules now exists (backend/user-management/app/api/v1/
//  endpoints/trading_settings.py, gateway-registered) and the parent component (below) loads/
//  saves through it. `onVerbosityChange` stays a plain callback prop so this presentational row
//  itself has zero API awareness.
const VerbosityToggleRow = ({ verbosity, onVerbosityChange }) => {
    const isVerbose = verbosity !== 'act_only';
    return (
        <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-bg-3 border border-[var(--divider)]">
            <div className="flex items-center gap-2 min-w-0">
                <FiEye size={12} className="text-fg-3 flex-shrink-0" />
                <div className="min-w-0">
                    <div className="text-xs font-semibold text-fg-1">Exit-lane notifications</div>
                    <div className="text-[10px] text-fg-3">
                        {isVerbose
                            ? 'Verbose — every chase attempt + ceiling alert'
                            : 'Act-only — ceiling alert only'}
                    </div>
                </div>
            </div>
            <Toggle
                checked={isVerbose}
                onChange={(next) => onVerbosityChange?.(next ? 'verbose' : 'act_only')}
                aria-label="Exit-lane notification verbosity"
            />
        </div>
    );
};

/*>>> EXIT-EXECUTION POLICY SUMMARY (read-only preview — pre-deploy / paper) <<<*/
//! The 5 chase/profit-lock/EOD values (StrategyDeployment.policy_overrides) only become EDITABLE
//  once a LIVE deployment exists — the PATCH contract is deployment-scoped
//  (deployments.py::update_deployment_policy needs a deployment_id). Pre-deploy and for paper we
//  show them READ-ONLY so the operator can SEE the policy that will apply to live orders; the
//  editable DeploymentSettingsForm renders in its place once live-deployed. Making these editable
//  at build-time (before any deployment exists) needs the deploy call to carry the overrides —
//  tracked as the follow-up per-account Step-4 settings work, not faked here.
const ExitPolicySummary = ({ values, brokerNames }) => {
    const v = { ...DEFAULT_SETTINGS, ...(values || {}) };
    const pctOrDash = (x) => (x === '' || x == null ? '—' : `${(Number(x) * 100).toFixed(2)}%`);
    const rows = [
        ['Chase attempts', v.chase_n ?? '—'],
        ['Chase interval', v.chase_interval_s == null ? '—' : `${v.chase_interval_s}s`],
        ['Chase cap', pctOrDash(v.chase_cap_pct)],
        ['Profit-lock', v.profit_lock_threshold_pct === '' || v.profit_lock_threshold_pct == null
            ? 'off'
            : `${(Number(v.profit_lock_threshold_pct) * 100).toFixed(0)}%`],
        ['EOD close owner', eodCloseOwnerLabel(v.eod_close_owner, brokerNames)],
        ...(v.eod_close_owner === 'platform_first'
            ? [['Pre-cutoff exit lead', v.exit_lead_minutes ? `${v.exit_lead_minutes}m` : '—']]
            : []),
    ];
    const summary = `Chase ×${v.chase_n ?? '—'} · Lock ${v.profit_lock_threshold_pct === '' || v.profit_lock_threshold_pct == null ? 'off' : `${(Number(v.profit_lock_threshold_pct) * 100).toFixed(0)}%`}`;
    return (
        <CollapsibleCard icon={FiSliders} title="Exit-execution policy" summary={summary}>
            <p className="text-[10px] text-fg-3 px-1">Applies to live orders. Editable once deployed live.</p>
            <div className="grid grid-cols-2 gap-1.5">
                {rows.map(([label, val]) => (
                    <div key={label} className="p-2 rounded-lg bg-bg-3 border border-[var(--divider)]">
                        <div className="text-[10px] text-fg-3">{label}</div>
                        <div className="text-xs font-semibold text-fg-1">{val}</div>
                    </div>
                ))}
            </div>
        </CollapsibleCard>
    );
};

/*>>> FIXED PLATFORM SAFETY POLICIES (read-only transparency) <<<*/
//! Platform-managed order-execution safety logics — hardcoded / global BY DESIGN (NOT per-strategy
//  configurable; verified against the OMS: live_executor _BIS_MAX_RETRIES + reject_classifier,
//  exit_policy_table, marketable_limit_conversion, pre_trade_risk_gate). Surfaced READ-ONLY so the
//  operator can see EVERY logic that acts on an order — including the ones they cannot change
//  (Deepak: "Chase and Retry and all other logics should surface"). Descriptions are QUALITATIVE
//  on purpose: there is no single served source for these constants today, so we describe the
//  policy rather than mirror a number that could silently drift from the backend
//  (feedback_constant_in_prod_makes_tests_vacuous — a toggle/figure that controls nothing, or lies,
//  is worse than none). If any of these ever needs to be user-tunable it requires a backend change
//  first — do not fake it as a live control.
const FIXED_POLICIES = [
    { name: 'Placement retry', desc: 'Automatic, but retried ONLY on a pre-send network failure. Any reject that could have reached the broker is never retried — capital-critical.' },
    { name: 'Exit chase eligibility', desc: 'Stop-loss legs chase aggressively toward the touch; take-profit legs rest patiently (no chase). Fixed per leg type.' },
    { name: 'Marketable-limit exits', desc: 'Exit-class MARKET orders are sent as a marketable LIMIT with a small aggression buffer — both brokers reject a bare MARKET on the exit lane.' },
    { name: 'Fat-finger price band', desc: 'An order priced too far from the reference price is blocked before dispatch.' },
    { name: 'Stale-price gate', desc: 'Dispatch is blocked when the reference price is older than the platform freshness window.' },
];

const FixedPlatformPoliciesSection = () => (
    <CollapsibleCard icon={FiLock} title="Fixed platform safety policies" summary={`${FIXED_POLICIES.length} always-on`}>
        <p className="text-[10px] text-fg-3 px-1">Always-on, platform-managed — not user-configurable.</p>
        <div className="space-y-1.5">
            {FIXED_POLICIES.map((p) => (
                <div key={p.name} className="p-2 rounded-lg bg-bg-3 border border-[var(--divider)]">
                    <div className="text-xs font-semibold text-fg-1">{p.name}</div>
                    <div className="text-[10px] text-fg-3 mt-0.5 leading-snug">{p.desc}</div>
                </div>
            ))}
        </div>
    </CollapsibleCard>
);

/*>>> DRIFT DIFF VIEW — the ONE human-readable previous-vs-new rendering (banner + 409 panel) <<<*/
//--> "order_type" -> "Order type": every underscore replaced, first letter capitalised. Labels
//    come from the server's own keys; the FE enumerates no field set.
const driftFieldLabel = (key) => {
    const t = String(key || '').replace(/_/g, ' ');
    return t.charAt(0).toUpperCase() + t.slice(1);
};
const driftValue = (v) => (v === null || v === undefined || v === '' ? '\u2014' : String(v));

const DriftDiffView = ({ divergedAccounts, names }) => {
    const accounts = Array.isArray(divergedAccounts) ? divergedAccounts : [];
    return (
        <div data-testid="drift-diff-view" className="flex flex-col gap-2">
            {accounts.map((acct) => {
                //--> one-sided accounts: a normal "Included in deployment" Yes/No row (carry_over
                //    re-materialises the LAST deployment's accounts, so Previous must be readable)
                const rows = acct.presence === 'running_only'
                    ? [{ label: 'Included in deployment', prev: 'Yes', next: 'No' }]
                    : acct.presence === 'draft_only'
                        ? [{ label: 'Included in deployment', prev: 'No', next: 'Yes' }]
                        : Object.entries(acct.fields || {}).map(([field, vals]) => ({
                            label: driftFieldLabel(field),
                            prev: driftValue(vals?.deployed),
                            next: driftValue(vals?.draft),
                        }));
                return (
                    <div key={acct.account_id} className="text-xs">
                        <div className="font-semibold text-fg-1 truncate" title={acct.account_id}>
                            {acctDisplay(acct.account_id, names)}
                        </div>
                        <table className="w-full text-left mt-1">
                            <thead>
                                <tr className="text-fg-3">
                                    <th className="font-medium pr-2">Setting</th>
                                    <th className="font-medium pr-2">Previous (deployed)</th>
                                    <th className="font-medium">New (this version)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.label}>
                                        <td className="pr-2 text-fg-2">{r.label}</td>
                                        <td className="pr-2 text-fg-1">{r.prev}</td>
                                        <td data-changed="true" className="text-warning font-semibold">{r.next}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
};

/*>>> DRIFT RESOLUTION PANEL — opened only from a server 409 DEPLOY_DRIFT_UNRESOLVED <<<*/
const DriftResolutionPanel = ({ driftPrompt, names, onResolveDrift, onDismissDrift }) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => onDismissDrift()}>
        <div
            role="dialog"
            aria-label="Review setting changes"
            className="bg-white dark:bg-bg-2 rounded-2xl p-6 max-w-2xl mx-4 shadow-2xl border border-[var(--divider)]"
            onClick={e => e.stopPropagation()}
        >
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[var(--badge-warn-bg)] rounded-xl flex items-center justify-center border border-[color-mix(in_oklch,var(--color-loss)_25%,transparent)] shrink-0">
                        <FiAlertTriangle className="text-loss" size={22} />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-fg-1">Settings changed since the last deployment</h3>
                        <p className="text-xs text-fg-3 mt-1">
                            Each button deploys one whole column: the accounts and settings listed under it.
                        </p>
                    </div>
                </div>
                <div className="rounded-xl border border-[var(--divider)] bg-bg-3 p-3 max-h-72 overflow-auto">
                    <DriftDiffView divergedAccounts={driftPrompt.divergedAccounts} names={names} />
                </div>
                <div className="flex flex-col gap-2 pt-1">
                    <button type="button" data-testid="drift-use-new" onClick={() => onResolveDrift('use_new')} className="btn btn-primary w-full justify-start text-left">
                        <FiFileText size={14} />
                        <span>Use new settings and deploy</span>
                    </button>
                    <button type="button" data-testid="drift-keep-previous" onClick={() => onResolveDrift('carry_over')} className="btn btn-secondary w-full justify-start text-left">
                        <FiShield size={14} />
                        <span>Keep previous settings and deploy</span>
                    </button>
                    <button type="button" data-testid="drift-cancel" onClick={() => onDismissDrift()} className="btn btn-ghost w-full text-fg-3 text-xs">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
);

/*>>> R6.3 — PRE-DEPLOY DRIFT SURFACE (per-account-order-settings, 02-design.md §2 R6.3) <<<*/
//! QA loop-back-3 (2026-07-28) R6.3 — TRUTHFUL banner on the reachable stop-first flow.
//  The prior banner read GET /strategies/{id}/allocations, which after a draft save resolves to
//  the NEW (unmaterialized) draft version → returns allocations=[] → banner blank in exactly the
//  mandated stop→edit→redeploy flow (R6.1 + R1.2.1 autosave). It now reads the server drift-PREVIEW
//  (GET /strategies/{id}/allocations/drift-preview), which runs the SAME compare_running_vs_draft
//  the authoritative deploy() 409 gate runs — the saved draft's config.orders vs the LAST
//  deployment's materialized VALUES — and returns the per-account divergence directly. The banner
//  renders "deployed: 5 · draft: 1" per diverged field, and shows NOTHING when the draft matches
//  the last deployment (or there is no prior deployment). Never blocks Deploy; "Review and deploy"
//  runs the normal deploy path (Go Live confirm + ack still apply);
//  the authoritative gate remains the server-side 409 DEPLOY_DRIFT_UNRESOLVED. Uses the shared
//  gateway-routed `api` client directly (routed through the gateway — never a direct microservice
//  call). `draftOrders` is retained on the signature for callers but is no longer the divergence
//  source — the server preview is authoritative (the FE cannot see the LAST deployment's rows).
const PreDeployDriftBanner = ({ strategyId, draftOrders, onReviewDeploy, reviewDisabled }) => { // eslint-disable-line no-unused-vars
    const [divergedAccounts, setDivergedAccounts] = useState(null); // null = not yet loaded
    const names = useAccountNames();

    useEffect(() => {
        if (!strategyId) { setDivergedAccounts(null); return undefined; }
        //! Defensive: some test harnesses mock only `{ services }` from this module (no default
        //  export) — never throw synchronously on an unmocked/absent client, just skip the fetch.
        if (typeof api?.get !== 'function') return undefined;
        let cancelled = false;
        api.get(`/strategies/${strategyId}/allocations/drift-preview`)
            .then((res) => {
                if (cancelled) return;
                const payload = res?.data?.data ?? res?.data;
                const accts = Array.isArray(payload?.diverged_accounts) ? payload.diverged_accounts : [];
                setDivergedAccounts(accts);
            })
            .catch((err) => {
                if (cancelled) return;
                setDivergedAccounts([]);
                console.log('[DIAG] PreDeployDriftBanner drift-preview fetch error', { err: err?.message, strategyId });
            });
        return () => { cancelled = true; };
    }, [strategyId]);

    //--> Show NOTHING when not loaded, no strategy, or no divergence (brief R6.3: "shows nothing
    //    when there's no divergence"). The divergence values are server-computed against the LAST
    //    deployment, so the banner is truthful even in the stop-first flow.
    if (!strategyId || divergedAccounts === null || divergedAccounts.length === 0) return null;

    const n = divergedAccounts.length;

    return (
        <div className="px-3 py-2 rounded-lg bg-bg-3 border border-[var(--divider)] text-[11px] text-fg-3 flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <FiAlertTriangle size={12} className="text-warning flex-shrink-0" />
                <span className="font-semibold text-fg-2">Per-account order settings</span>
            </div>
            <div className="flex flex-col gap-1.5 pt-1 border-t border-[var(--divider)]">
                <span className="text-warning font-semibold">
                    {n} account{n > 1 ? 's' : ''} differ from what&apos;s deployed:
                </span>
                <DriftDiffView divergedAccounts={divergedAccounts} names={names} />
                <button
                    type="button"
                    data-testid="drift-review-deploy"
                    disabled={reviewDisabled}
                    onClick={onReviewDeploy}
                    className="btn btn-secondary text-xs self-start"
                >
                    Review and deploy
                </button>
            </div>
        </div>
    );
};

/*>>> MAIN COMPONENT <<<*/
const DeploymentSection = ({
    strategyId,
    strategyStatus,
    currentVersionId,
    currentVersionNumber,
    readinessItems,
    hasUnsavedChanges,
    deploymentState,
    onModeChange,
    onDeploy,
    onStop,
    draftOrders,
    driftPrompt,
    onResolveDrift,
    onDismissDrift,
}) => {
    const {
        selectedMode,
        activeDeployment,
        hasOpenPositions,
        isDeploying,
        isStopping,
    } = deploymentState;

    //--> Holder·broker names for the drift-resolution modal rows (never the raw UUID).
    const accountNames = useAccountNames();

    //--> Determine deploy button state
    const isDraft = strategyStatus === 'draft';
    const hasActiveDeployment = !!activeDeployment;

    //! strategy-deploy-readiness-checklist R5.4/R5.5 (02-design.md §6) — deploy is disabled
    //  while any REQUIRED readiness item fails, in BOTH paper and live mode (server
    //  deployment_service enforces REQUIRED for paper too — dead-exit-readiness-gate F4).
    //  The RECOMMENDED-item acknowledgement flow stays LIVE-only (unchanged). `tallyValid` is
    //  REMOVED from this expression entirely — R2.4.2's `sizing_balanced` readiness item now
    //  folds that same signal into `anyRequiredFailing`.
    const anyRequiredFailing = (readinessItems || []).some((i) => i.severity === 'REQUIRED' && i.pass === false);
    const deployDisabled = isDraft || hasUnsavedChanges || hasActiveDeployment || isDeploying
        || anyRequiredFailing;

    const deployTooltip = useMemo(() => {
        if (isDraft) return 'Run a backtest first';
        if (hasUnsavedChanges) return 'Save strategy first';
        if (hasActiveDeployment) return 'Stop current deployment first';
        if (anyRequiredFailing) return 'Fix required readiness items first';
        return '';
    }, [isDraft, hasUnsavedChanges, hasActiveDeployment, anyRequiredFailing]);

    const modeLabel = selectedMode === 'live' ? 'Live Trade' : 'Paper Trade';

    //--> Live mode confirmation
    const [showLiveConfirm, setShowLiveConfirm] = useState(false);
    const [showStopConfirm, setShowStopConfirm] = useState(false);

    //! R7.3 — exit-lane notification verbosity. Persisted via user-management's
    //  /trading-settings/notification-rules (Tranche-B integration fix — see VerbosityToggleRow's
    //  header comment for the endpoint discovery/wiring history). User-scoped (not per-
    //  strategy/per-deployment), loaded ONCE on mount regardless of which strategy is open.
    const [verbosity, setVerbosityLocal] = useState('verbose');
    useEffect(() => {
        let cancelled = false;
        services.liveExitLane.getNotificationVerbosity()
            .then((res) => {
                if (cancelled) return;
                const payload = res?.data?.data ?? res?.data;
                if (payload?.exit_lane_verbosity) setVerbosityLocal(payload.exit_lane_verbosity);
            })
            .catch((err) => {
                console.log('[DIAG] DeploymentSection verbosity load error', { err: err?.message });
            });
        return () => { cancelled = true; };
    }, []);
    const setVerbosity = useCallback((next) => {
        setVerbosityLocal(next); // optimistic
        services.liveExitLane.setNotificationVerbosity(next).catch((err) => {
            console.log('[DIAG] DeploymentSection verbosity save error', { err: err?.message });
        });
    }, []);

    //--> Brief success flash after deploy completes
    const [justDeployed, setJustDeployed] = useState(false);
    const prevDeployingRef = useRef(false);

    useEffect(() => {
        //--> Detect transition: isDeploying true→false AND activeDeployment appeared
        if (prevDeployingRef.current && !isDeploying && activeDeployment) {
            setJustDeployed(true);
            const timer = setTimeout(() => setJustDeployed(false), 3000);
            prevDeployingRef.current = isDeploying;
            return () => clearTimeout(timer);
        }
        prevDeployingRef.current = isDeploying;
    }, [isDeploying, activeDeployment]);

    //--> Reset live confirm modal when deploy starts
    useEffect(() => {
        if (isDeploying) setShowLiveConfirm(false);
    }, [isDeploying]);

    //--> R6.2.2 drift prompt state + resolution live in StrategyBuilder (single deploy chokepoint);
    //    this section only renders DriftResolutionPanel from the `driftPrompt` prop.
    const handleDeploy = () => {
        if (selectedMode === 'live') {
            setShowLiveConfirm(true);
        } else {
            onDeploy();
        }
    };

    const confirmLiveDeploy = () => {
        setShowLiveConfirm(false);
        onDeploy();
    };

    const isPaper = selectedMode === 'paper';

    return (
        <div className="space-y-4">
            {/*>>> Section Header <<<*/}
            <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-md bg-[var(--theme-purple)] flex items-center justify-center shadow-sm">
                    <FiShield size={12} className="text-white" />
                </div>
                <h4 className="text-xs font-bold text-fg-3 uppercase tracking-widest">Deployment</h4>
            </div>

            {/*>>> Version Info <<<*/}
            <VersionInfoCard
                currentVersionNumber={currentVersionNumber}
                activeDeployment={activeDeployment}
            />

            {/*>>> R6.3 — pre-deploy drift surface <<<*/}
            <PreDeployDriftBanner strategyId={strategyId} draftOrders={draftOrders} onReviewDeploy={handleDeploy} reviewDisabled={deployDisabled} />

            {/*>>> Order-execution transparency + policy (VISIBLE WHILE CONFIGURING — no longer
                gated on a live deployment). "Let the user SEE what type of order will be placed at
                the broker, and every execution logic that acts on it" (Deepak, 2026-07-28).
                • Per-account protection = the CONFIRMED GET /protection-routing endpoint, composed
                  server-side (no FE native/synthetic string construction — auto-flips to "native"
                  when Part B's live probes flip the FT flags, zero FE change).
                • Exit-execution policy: editable form once live-deployed, read-only preview
                  otherwise (the PATCH contract is deployment-scoped — pre-deploy editing is the
                  follow-up per-account Step-4 work).
                • Fixed platform safety policies: read-only (hardcoded/global by design). <<<*/}
            {strategyId && (
                <div className="space-y-4 p-3 rounded-xl bg-bg-2 border border-[var(--divider)]">
                    <ProtectionTransparencySection strategyId={strategyId} />
                    {hasActiveDeployment && activeDeployment?.mode === 'live' ? (
                        <>
                            <DeploymentSettingsForm
                                strategyId={strategyId}
                                deploymentId={activeDeployment?.deployment_id}
                                initialSettings={activeDeployment?.policy_overrides}
                                brokerNames={brokerNamesForDeployment(draftOrders, accountNames)}
                            />
                            <VerbosityToggleRow
                                verbosity={verbosity}
                                onVerbosityChange={setVerbosity}
                            />
                        </>
                    ) : (
                        <ExitPolicySummary
                            values={activeDeployment?.policy_overrides}
                            brokerNames={brokerNamesForDeployment(draftOrders, accountNames)}
                        />
                    )}
                    <FixedPlatformPoliciesSection />
                </div>
            )}

            {/*>>> Deploying Progress Card <<<*/}
            {isDeploying && !hasActiveDeployment && (
                <div className={`p-4 rounded-xl border space-y-3 ${
                    isPaper
                        ? 'bg-[var(--badge-info-bg)] border-[color-mix(in_oklch,var(--badge-info-dot)_25%,transparent)]'
                        : 'bg-profit/10 border-profit/25'
                }`}>
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isPaper
                                ? 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)]'
                                : 'bg-profit/10 text-profit'
                        }`}>
                            <FiLoader size={16} className="animate-spin" />
                        </div>
                        <div>
                            <div className="text-sm font-bold text-fg-1">
                                Deploying {currentVersionNumber} as {modeLabel}...
                            </div>
                            <div className="text-xs text-fg-3 mt-0.5">Setting up your strategy</div>
                        </div>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1 rounded-full bg-bg-3 overflow-hidden">
                        <div className={`h-full rounded-full animate-pulse ${
                            isPaper
                                ? 'bg-gradient-to-r from-[var(--badge-info-dot)] to-[var(--badge-info-dot)]'
                                : 'bg-gradient-to-r from-profit to-profit'
                        }`} style={{ width: '60%' }} />
                    </div>
                </div>
            )}

            {/*>>> Deploy Success Flash <<<*/}
            {justDeployed && hasActiveDeployment && (
                <div className={`flex items-center gap-2.5 p-3 rounded-xl text-sm font-medium ${
                    activeDeployment?.mode === 'paper'
                        ? 'bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)] border border-[color-mix(in_oklch,var(--badge-info-dot)_25%,transparent)]'
                        : 'bg-profit/10 text-profit border border-profit/25'
                }`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activeDeployment?.mode === 'paper' ? 'bg-[var(--badge-info-dot)]' : 'bg-profit'
                    }`}>
                        <FiCheck size={11} className="text-white" />
                    </div>
                    Strategy deployed as {activeDeployment?.mode === 'paper' ? 'Paper Trade' : 'Live Trade'}!
                </div>
            )}

            {/*>>> Active Deployment Card <<<*/}
            <ActiveDeploymentCard
                deployment={activeDeployment}
                hasOpenPositions={hasOpenPositions}
                isStopping={isStopping}
                onStop={() => setShowStopConfirm(true)}
            />

            {/*>>> Mode Toggle (only show when no active deployment) <<<*/}
            {!hasActiveDeployment && !isDeploying && (
                <>
                    <ModeToggle
                        mode={selectedMode}
                        onChange={onModeChange}
                        disabled={isDeploying}
                    />

                    {/*>>> Mode Description <<<*/}
                    <div className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-xs min-h-[52px] ${
                        isPaper
                            ? 'bg-[color-mix(in_srgb,var(--badge-info-dot)_12%,transparent)] text-[var(--badge-info-fg)]'
                            : 'bg-[color-mix(in_srgb,var(--badge-profit-dot)_12%,transparent)] text-[var(--badge-profit-fg)]'
                    }`}>
                        {isPaper ? (
                            <>
                                <FiFileText size={13} className="flex-shrink-0 mt-0.5" />
                                <span>Simulated trading — no real orders will be placed. Use this to validate your strategy.</span>
                            </>
                        ) : (
                            <>
                                <FiRadio size={13} className="flex-shrink-0 mt-0.5" />
                                <span>Real money trading — orders will be executed on your connected broker account.</span>
                            </>
                        )}
                    </div>

                    {/*>>> Deploy Button <<<*/}
                    <Tooltip content={deployTooltip} placement="top" className="w-full">
                    <button
                        type="button"
                        onClick={handleDeploy}
                        disabled={deployDisabled}
                        className={`btn w-full ${isPaper ? 'btn-deploy-paper' : 'btn-deploy-live'}`}
                    >
                        {isDeploying ? (
                            <><FiLoader className="animate-spin" size={14} /> Deploying...</>
                        ) : (
                            <>
                                <FiPlay size={14} />
                                Deploy {currentVersionNumber || ''} as {modeLabel}
                            </>
                        )}
                    </button>
                    </Tooltip>
                </>
            )}

            {/*>>> Live Confirmation Dialog <<<*/}
            {showLiveConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowLiveConfirm(false)}>
                    <div className="bg-white dark:bg-bg-2 rounded-2xl p-6 max-w-sm mx-4 shadow-2xl border border-[var(--divider)]" onClick={e => e.stopPropagation()}>
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-[var(--badge-warn-bg)] to-[var(--badge-loss-bg)] rounded-2xl flex items-center justify-center mx-auto border border-[color-mix(in_oklch,var(--color-loss)_25%,transparent)]">
                                <FiAlertTriangle className="text-loss" size={28} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-fg-1">Go Live?</h3>
                                <p className="text-sm text-fg-3 mt-1.5">This will execute real orders with real money on your broker account.</p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowLiveConfirm(false)}
                                    className="btn btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmLiveDeploy}
                                    className="btn btn-deploy-live flex-1"
                                >
                                    <FiRadio size={14} />
                                    Confirm Live
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/*>>> R6.2.2 — Pre-deploy DRIFT RESOLUTION choice (carry-over vs use-new). Shown when
                the server rejected the redeploy 409 because the RUNNING deployment's per-account
                settings differ from this version's. The operator MUST choose; the choice is wired
                into the re-submitted deploy's drift_resolution. <<<*/}
            {driftPrompt && (
                <DriftResolutionPanel driftPrompt={driftPrompt} names={accountNames} onResolveDrift={onResolveDrift} onDismissDrift={onDismissDrift} />
            )}

            {/*>>> Stop Confirmation Dialog — stopping a running deployment is
                destructive (halts trading), so it needs a confirm just like Go Live. <<<*/}
            {showStopConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowStopConfirm(false)}>
                    <div className="bg-white dark:bg-bg-2 rounded-2xl p-6 max-w-sm mx-4 shadow-2xl border border-[var(--divider)]" onClick={e => e.stopPropagation()}>
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-[var(--badge-warn-bg)] to-[var(--badge-loss-bg)] rounded-2xl flex items-center justify-center mx-auto border border-[color-mix(in_oklch,var(--color-loss)_25%,transparent)]">
                                <FiSquare className="text-loss" size={26} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-fg-1">
                                    Stop {activeDeployment?.mode === 'paper' ? 'Paper Trade' : 'Live Trade'}?
                                </h3>
                                <p className="text-sm text-fg-3 mt-1.5">
                                    {activeDeployment?.mode === 'paper'
                                        ? `This stops the running strategy ${activeDeployment?.version_number || ''}. It will place no further simulated orders.`
                                        : `This stops your LIVE strategy ${activeDeployment?.version_number || ''} — it will immediately stop placing real orders on your broker account.`}
                                </p>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowStopConfirm(false)}
                                    className="btn btn-secondary flex-1"
                                >
                                    Keep Running
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowStopConfirm(false); onStop(); }}
                                    className="btn btn-danger flex-1"
                                >
                                    <FiSquare size={13} />
                                    Stop {activeDeployment?.mode === 'paper' ? 'Paper' : 'Live'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeploymentSection;
